import {
  Classes,
  Dialog,
  Intent,
  Button,
  FormGroup,
  TextArea,
  Callout,
} from '@blueprintjs/core';
import type { AxiosError } from 'axios';
import axios from 'axios';
import * as React from 'react';

import { useAppDispatch, useAppSelector } from 'vms/app/hooks';
import FormHelper from 'vms/common/components/FormHelper';
import { getFormErrors } from 'vms/common/errors';
import { useHasPermission } from 'vms/features/accounts/permissions';
import { vehicleInStatusCreated } from 'vms/features/vehicles/actions';

import { createNoteSuccess, closeNoteCreator } from './actions';
import { NOTE_PERMISSION_ERROR, NOTE_SAVE_ERROR_MESSAGES } from './errors';
import { selectNoteCreatorData } from './selectors';
import type { Note } from './types';
import { NoteModel } from './types';

export default function NoteCreator() {
  const dispatch = useAppDispatch();
  const canAddNote = useHasPermission()('addNotes');
  const noteCreatorData = useAppSelector(selectNoteCreatorData);
  const [text, setText] = React.useState('');
  const [errors, setErrors] = React.useState<ValidationErrors<Note>>({});
  const [saveInProgress, setSaveInProgress] = React.useState(false);

  const close = () => {
    setText('');
    setErrors({});
    setSaveInProgress(false);
    dispatch(closeNoteCreator());
  };

  const save = () => {
    if (!noteCreatorData || !canAddNote || saveInProgress) {
      return;
    }
    setSaveInProgress(true);
    setErrors({});
    axios
      .post<Note>('/notes/', {
        text,
        ...noteCreatorData.params,
      })
      .then(
        (response) => {
          dispatch(createNoteSuccess(response.data));
          if (response.data.model === NoteModel.VehicleInStatus) {
            // The request uses Vehicle.id; the response identifies the created VehicleInStatus.
            const requestId = noteCreatorData.params.object_id;
            const vehicleId = typeof requestId === 'string' ? requestId : requestId[0];
            dispatch(vehicleInStatusCreated(response.data.object_id, vehicleId));
          }
          close();
          noteCreatorData.afterNoteAdd?.();
        },
        (error: AxiosError<ValidationErrors<Note>>) => {
          setSaveInProgress(false);
          setErrors(getFormErrors(error, NOTE_SAVE_ERROR_MESSAGES));
        }
      );
  };

  const formHelper = new FormHelper(errors);
  return (
    <Dialog
      icon="inbox"
      isOpen={Boolean(noteCreatorData)}
      onClose={close}
      title="Add note"
    >
      <div className={Classes.DIALOG_BODY}>
        {!canAddNote && (
          <Callout intent="warning" role="alert">
            {NOTE_PERMISSION_ERROR}
          </Callout>
        )}
        <FormGroup {...formHelper.getErrors('non_field_errors')} />
        <FormGroup {...formHelper.getErrors('text')}>
          <TextArea
            name="text"
            rows={5}
            fill={true}
            intent={formHelper.getIntent('text')}
            onChange={(event) => setText(event.target.value)}
            value={text}
            readOnly={!canAddNote}
          />
        </FormGroup>
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button text="Cancel" onClick={close} disabled={saveInProgress} />
          <Button
            intent={Intent.PRIMARY}
            onClick={save}
            text="Add"
            disabled={saveInProgress || !canAddNote}
          />
        </div>
      </div>
    </Dialog>
  );
}
